import React, { useState } from 'react'
import { Button, Form, TextArea } from 'semantic-ui-react'
import { createActivity } from 'utility/services/crm'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmToast } from 'hooks/crm'
import { ChipButton, Row } from './ui'

const FOLLOW_UPS = [
    [0, 'No follow-up'],
    [1, '1 day'],
    [3, '3 days'],
    [7, '1 week'],
    [14, '2 weeks'],
]

const TYPES = [
    ['note', 'Note'],
    ['call', 'Call'],
]

/**
 * Quick note / call log with an optional "follow up in N days" task.
 * @param {{ contactId: number }} props
 */
export const QuickLog = ({ contactId }) => {
    const { successToast, showError } = useCrmToast()
    const [type, setType] = useState('note')
    const [title, setTitle] = useState('')
    const [body, setBody] = useState('')
    const [days, setDays] = useState(0)
    const [saving, setSaving] = useState(false)

    const label = TYPES.find(([key]) => key === type)[1]
    const canSave = body.trim().length > 0 && !saving

    const submit = async event => {
        event.preventDefault()
        if (!canSave) return
        setSaving(true)
        try {
            await createActivity({
                Type: type,
                ContactId: contactId,
                Title: title.trim() || label,
                Body: body.trim(),
                FollowUpInDays: days || undefined,
            })
            successToast(`${label} logged${days ? ` · follow-up in ${days} day${days === 1 ? '' : 's'}` : ''}`)
            setTitle('')
            setBody('')
            setDays(0)
            invalidateCrm(...ALL_CRM_SCOPES)
        } catch (err) {
            showError(err)
        } finally {
            setSaving(false)
        }
    }

    return (
        <Form onSubmit={submit} aria-label="Log an activity" style={{ marginBottom: 16 }}>
            <Row $gap={6} role="group" aria-label="Activity type" style={{ marginBottom: 8 }}>
                {TYPES.map(([key, text]) => (
                    <ChipButton key={key} type="button" $active={type === key} aria-pressed={type === key} onClick={() => setType(key)}>
                        {text}
                    </ChipButton>
                ))}
            </Row>
            <Form.Field>
                <input
                    aria-label="Title (optional)"
                    placeholder={`Title (optional, defaults to “${label}”)`}
                    value={title}
                    maxLength={120}
                    onChange={event => setTitle(event.target.value)}
                />
            </Form.Field>
            <Form.Field>
                <TextArea
                    aria-label="Details"
                    placeholder="Details"
                    rows={3}
                    value={body}
                    onChange={(_, d) => setBody(d.value)}
                />
            </Form.Field>
            <Row $between $wrap>
                <Row $gap={6} $wrap role="group" aria-label="Follow up in">
                    <span style={{ opacity: 0.7, fontSize: '0.85rem' }}>Follow up in</span>
                    {FOLLOW_UPS.map(([value, text]) => (
                        <ChipButton key={value} type="button" $active={days === value} aria-pressed={days === value} onClick={() => setDays(value)}>
                            {text}
                        </ChipButton>
                    ))}
                </Row>
                <Button type="submit" primary size="small" loading={saving} disabled={!canSave}>
                    Log {label.toLowerCase()}
                </Button>
            </Row>
        </Form>
    )
}
